import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';

import UserForm from "../components/UserForm";
import "../styles/UserForm.css";
import { validateEmail } from "../utils/validators";


const CreateUser = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [designation, setDesignation] = useState("");
  const [location, setLocation] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [userType, setUserType] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOutletOptions, setSelectedOutletOptions] = useState([]);

  const [errors, setErrors] = useState({
      username: "",
      password: "",
  });

  const auth = JSON.parse(localStorage.getItem("auth"));
  const isAdmin = auth?.user?.user_type === "Admin";

  const allowedLocations = useMemo(
    () => auth?.user?.locations,
    [auth]
  );

  const locationOptions = allowedLocations.map(loc => ({
      value: loc,
      label: loc
  }));

  const allOutletsByLocation = useMemo(() => {
    return auth?.user?.branches;
  }, [auth]);

  const groupedOutletOptions = useMemo(() => {
    if (!location.length) return [];

    return location.map((loc) => ({
      label: loc,
        options: (allOutletsByLocation[loc]).map((outlet) => ({
        value: outlet,
        label: outlet,
        location: loc,
      })),
    }));
  }, [location, allOutletsByLocation]);

  const allOutletOptionsFlat = useMemo(() => {
    return groupedOutletOptions.flatMap((group) => group.options);
  }, [groupedOutletOptions]);

  const allOutletValues = useMemo(() => {
    return groupedOutletOptions.flatMap(group =>
      group.options.map(opt => opt.value)
    );
  }, [groupedOutletOptions]);

  const handleUsernameChange = (e) => {
    const value = e.target.value;
      setUsername(value);
      setErrors((prev) => ({
      ...prev,
      username: validateEmail(value),
    }));
  };

  const handleCreateUser = async() => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!username || !designation || !userType || location.length === 0 || outlets.length === 0) {
      setErrorMessage("Please fill in all required fields");

      // Clear error message after 5 seconds
      setTimeout(() => {
          setErrorMessage("");
      }, 5000);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`/api/create_user`, {
          auth_user: JSON.parse(localStorage.getItem('auth')),
          username: username,
          designation: designation,
          location: location,
          user_type: userType,
          outlets: outlets
      });

      console.log("response", response);
      if (response.status === 201) {
          setSuccessMessage("User created successfully");
          // Clear error message after 5 seconds
          setTimeout(() => {
              navigate('/dashboard');
          }, 3000);
          setLoading(false);
      }
    } catch (error) {
      if (error.response) {
          setErrorMessage(error.response.data.error.message);
      } else {
          setErrorMessage("Something went wrong. Please try again.")
      }
      setLoading(false);
      // Clear error message after 5 seconds
      setTimeout(() => {
          setErrorMessage("");
      }, 5000);
    }
  };

  const SELECT_ALL = { label: "Select All", value: "__ALL__" };
  const locationOptionsWithSelectAll = [SELECT_ALL, ...locationOptions];
  const outletOptionsWithSelectAll = useMemo(() => {
    return allOutletValues.length
        ? [SELECT_ALL, ...groupedOutletOptions]
        : groupedOutletOptions;
  }, [allOutletValues, groupedOutletOptions]);
  
  const handleLocationChange = (selected) => {
    if (!selected || selected.length === 0) {
      setLocation([]);
      setSelectedOutletOptions([]);
      setOutlets([]);
      return;
    }

    let selectedLocationValues = [];

    if (selected.some(opt => opt.value === "__ALL__")) {
      selectedLocationValues = locationOptions.map(opt => opt.value);
    } else {
      selectedLocationValues = selected.map(opt => opt.value);
    }

    setLocation(selectedLocationValues);

    // Remove outlets that belong to removed locations
    setSelectedOutletOptions(prevSelected =>
      prevSelected.filter(outlet =>
      selectedLocationValues.includes(outlet.location)
      )
    );

    setOutlets(prevOutlets =>
      prevOutlets.filter((outletValue) => {
        const outletOption = allOutletOptionsFlat.find(
          (o) => o.value === outletValue
      );
        return (
          outletOption && selectedLocationValues.includes(outletOption.location)
        );
      })
    );
  };


  const handleOutletChange = (selected) => {
    if (!selected || selected.length === 0) {
      setSelectedOutletOptions([]);
      setOutlets([]);
      return;
    }

    // Select All clicked
    if (selected.some(opt => opt.value === "__ALL__")) {
      setSelectedOutletOptions(allOutletOptionsFlat);
      setOutlets(allOutletValues);
      return;
    }

    // Normal selection
    setSelectedOutletOptions(selected);
    setOutlets(selected.map(opt => opt.value));
  };

  return (
    <div className="page">
      <UserForm
        title="Create User"
        subtitle="Enter user details below"
        buttonText="Create User"
        username={username}
        setUsername={handleUsernameChange}
        designation={designation}
        setDesignation={setDesignation}
        location={location}
        setLocation={setLocation}
        locationOptions={locationOptions}
        userType={userType}
        setUserType={setUserType}
        errors={errors}
        onSubmit={handleCreateUser}
        errorMessage={errorMessage}
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        loading={loading}
        isAdmin={isAdmin}
        locationOptionsWithSelectAll={locationOptionsWithSelectAll}
        handleLocationChange={handleLocationChange}
        outletOptionsWithSelectAll={outletOptionsWithSelectAll}
        handleOutletChange={handleOutletChange}
        selectedOutletOptions={selectedOutletOptions}
      />
    </div>
  );
};

export default CreateUser;
